import React, { useState } from 'react';
import { 
  Eye, EyeOff, Lock, Mail, ArrowRight, Shield, 
  Fingerprint, AlertCircle, Loader
} from 'lucide-react';

interface LoginCredentials {
  email: string;
  password: string;
}

interface Agent {
  id: string;
  name: string;
  phone: string;
  email: string;
  area: string;
  avatar: string;
  rating: number;
  collections: number;
  target: number;
  role: 'collector' | 'supervisor' | 'admin';
  department: string;
}

interface WasteAgentLoginProps {
  onLoginSuccess: (agent: Agent) => void;
}

const WasteAgentloginAdmin: React.FC<WasteAgentLoginProps> = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Demo credentials for testing
  const demoCredentials = [
    { 
      email: 'agent@waste.gov', 
      password: 'agent123', 
      role: 'collector',
      name: 'Mohammed Ali',
      avatar: '👨'
    },
    { 
      email: 'supervisor@waste.gov', 
      password: 'super123', 
      role: 'supervisor',
      name: 'Priya Sharma', 
      avatar: '👩‍💼'
    },
    { 
      email: 'admin@waste.gov', 
      password: 'admin123', 
      role: 'admin',
      name: 'Rajesh Kumar',
      avatar: '👨‍💼'
    }
  ];

  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) {
      setLoginError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Check demo credentials
    const validCredential = demoCredentials.find(
      cred => cred.email === loginForm.email && cred.password === loginForm.password
    );

    if (validCredential) {
      const loggedInAgent: Agent = {
        id: `AG${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        name: validCredential.name,
        phone: '9876543203',
        email: loginForm.email,
        area: 'Jubilee Hills - Sector 1',
        avatar: validCredential.avatar,
        rating: 4.8,
        collections: 156,
        target: 200,
        role: validCredential.role as Agent['role'],
        department: 'Waste Management'
      };

      // Save login if remember me is checked
      if (rememberMe) {
        localStorage.setItem('wasteAgentLogin', JSON.stringify({
          agent: loggedInAgent,
          timestamp: Date.now()
        }));
      }

      onLoginSuccess(loggedInAgent);
    } else {
      setLoginError('Invalid email or password. Please use the demo credentials provided below.');
    }

    setIsLoading(false);
  };

  const fillDemoCredentials = (email: string, password: string) => {
    setLoginForm({ email, password });
    setLoginError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl transform hover:scale-105 transition-transform duration-300">
            <Shield className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Waste Agent Portal</h1>
          <p className="text-gray-600 text-lg">Secure access for field agents</p>
          <div className="mt-4 flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-500">System Online</span>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In to Continue</h2>
            <p className="text-gray-500">Enter your credentials to access the portal</p>
          </div>

          <div className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="pl-12 pr-4 py-4 w-full border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-all duration-200 hover:bg-white"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="pl-12 pr-12 py-4 w-full border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-all duration-200 hover:bg-white"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 transition-colors"
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                  Remember me
                </span>
              </label>
              <button 
                type="button" 
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Forgot password?
              </button>
            </div>

            {/* Error Message */}
            {loginError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-2xl animate-pulse">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700">{loginError}</span>
                </div>
              </div>
            )}

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={isLoading || !loginForm.email || !loginForm.password}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-4 rounded-2xl font-bold text-lg flex items-center justify-center space-x-2 hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>

          {/* Demo Credentials Section */}
          <div className="mt-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
              <Fingerprint className="w-4 h-4" />
              <span>Demo Credentials - Click to Auto-Fill</span>
            </h4>
            <div className="space-y-3">
              {demoCredentials.map((cred, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => fillDemoCredentials(cred.email, cred.password)}
                  className="w-full flex justify-between items-center p-3 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{cred.avatar}</span>
                    <div className="text-left">
                      <div className="font-medium text-gray-900 capitalize">{cred.role}</div>
                      <div className="text-xs text-gray-500">{cred.name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600 font-mono">{cred.email}</div>
                    <div className="text-xs text-gray-400 font-mono">{cred.password}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                </button>
              ))}
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-xs text-yellow-700 text-center">
                💡 <strong>Tip:</strong> Click any credential above to auto-fill the login form
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-4">
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Secure</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Reliable</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span>Efficient</span>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Powered by Municipal Waste Management System
          </p>
          <p className="text-xs text-gray-400">
            © 2024 Waste Management Portal. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WasteAgentloginAdmin;